import React, { useState, useEffect, useCallback } from 'react';
import {
	Button,
	Card,
	PageHeader,
	Typography,
	Divider,
	Select,
	Form,
	Spin,
	message,
	Modal,
	Tooltip,
} from 'antd';

import Meta from '../../../../components/Meta';
import Fallback from '../../../../components/Fallback';
import Script from '../../../../components/Script';
import ConfigScriptStrs from '../../../../components/ConfigScriptStrs';
import ElementsTabs from '../../../../components/ElementsTabs';
import { Container, Grid, ScriptWrapper, ButtonContainer, OptionsContainer } from './styles';

import UsersAPI from '../../../../services/sdks/user';
import ShareAPI from '../../../../services/sdks/share';
import ScriptsAPI from '../../../../services/sdks/scripts';
import ProgramsAPI from '../../../../services/sdks/programs';
import ElementsAPI from '../../../../services/sdks/elements';

import { FiMaximize, FiMinimize, FiSave, FiSettings, FiXSquare } from 'react-icons/fi';

import { useScript } from '../../../../hooks';

import env from '../../../../config/env';
import SoundtracksAPI from '../../../../services/sdks/soundtracks';
import TKVoiceConfigAPI from '../../../../services/sdks/tkVoiceConfig';
import RecplaySegmentTemplatesAPI from '../../../../services/sdks/recplay-segment-templates';

const layout = { labelCol: { span: 6 }, wrapperCol: { span: 12 } };
const breadcrumb = {
	routes: [
		{ breadcrumbName: 'Painel administrativo' },
		{ breadcrumbName: 'Conteúdos' },
		{ breadcrumbName: 'Editar modelo' },
	],
	style: { marginBottom: 12 },
};

const EditScript = () => {
	const [users, setUsers] = useState([]);
	const [user, setUser] = useState(undefined);
	const [programs, setPrograms] = useState([]);
	const [scripts, setScripts] = useState([]);
	const [scriptId, setScriptId] = useState(undefined);
	const [fallback, setFallback] = useState({ initialData: true });
	const [showConfigStrsModal, setShowConfigStrsModal] = useState(false);
	const [tkVoiceTypes, setTkVoiceTypes] = useState({ caster: [], local: [] });
	const [recplaySegmentTemplates, setRecplaySegmentTemplates] = useState([]);

	const {
		cleanUp,
		setElements,
		script,
		setScript,
		program,
		setProgram,
		isSorting,
		setIsSorting,
		onElementMove,
		compact,
		setCompact,
		deletedSoundtracks,
	} = useScript();

	const handleUpdateScript = useCallback(async () => {
		try {
			setFallback((prev) => ({ ...prev, updatingScript: true }));
			const { body, name, weekDay } = script;
			await ScriptsAPI.modify({ scriptId, payload: { body, name, weekDay } });

			for (const deletedSoundtrackId of deletedSoundtracks) {
				await SoundtracksAPI.destroy(deletedSoundtrackId);
			}

			message.success('Modelo atualizado com sucesso', 2, () => window.location.reload());
		} catch (error) {
			console.error(error);
			message.error('Houve um erro, tente novamente');
			message.error(error?.response?.data?.message);

			setFallback((prev) => ({ ...prev, updatingScript: false }));
		}
	}, [script, scriptId, deletedSoundtracks]);

	useEffect(() => cleanUp(), [cleanUp]);

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				const {
					data: { users },
				} = await UsersAPI.index('active=true&pageNumber=0&pageSize=99999');

				setUsers(users);
				setFallback((prev) => ({ ...prev, initialData: false }));
			} catch (error) {
				console.error(error);
			}
		};

		fetchInitialData();
	}, []);

	useEffect(() => {
		const fetchTkVoiceTypes = async () => {
			try {
				const res = await TKVoiceConfigAPI.getPopulatedTypes(program._id, user?._id);
				setTkVoiceTypes(res.data.types);
			} catch (error) {
				setTkVoiceTypes({ caster: [], local: [] });
			}
		};

		if (program && user) {
			fetchTkVoiceTypes();
		}
	}, [program, user]);

	useEffect(() => {
		const fetchUserPrograms = async () => {
			try {
				setFallback((prev) => ({ ...prev, fetchingPrograms: true }));

				const {
					data: { programs },
				} = await ProgramsAPI.index({
					isActive: true,
					isDeleted: false,
					pageNumber: 0,
					pageSize: 99999,
					userId: user?._id,
				});
				const {
					data: { sharings },
				} = await ShareAPI.index(`user=${user?._id}`);

				setPrograms([
					...programs.map(({ userId, ...rest }) => ({ userId: userId?._id, ...rest })),
					...sharings.map(({ program }) => program),
				]);
				setFallback((prev) => ({ ...prev, fetchingPrograms: false }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os programas');
			}
		};

		if (user) {
			fetchUserPrograms();
		}
	}, [user]);

	useEffect(() => {
		const fetchProgramElements = async () => {
			try {
				setFallback((prev) => ({ ...prev, fetchingProgramElements: true }));

				const {
					data: { scripts },
				} = await ScriptsAPI.index({
					query: `program=${program?._id}&userId=${user?._id}`,
				});

				const {
					data: { elements },
				} = await ElementsAPI.index({ query: `userId=${user?._id}&program=${program?._id}` });

				const cashTracks = elements.filter(({ type }) => type === 'CASHTRACK');
				const customs = elements.filter(({ type }) => type === 'CUSTOM');
				const merchans = elements.filter(({ type }) => ['SPONSOR', 'OFFERING'].includes(type));
				const trackAds = elements.filter(({ type }) => type === 'TRACK-AD');

				setElements({ cashTracks, customs, merchans, trackAds });
				setScripts(scripts);
				setFallback((prev) => ({ ...prev, fetchingProgramElements: false }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os programas');
			}
		};

		const fetchRecplaySegmentTemplates = async () => {
			try {
				const res = await RecplaySegmentTemplatesAPI.getRecplayTemplates({
					pageNumber: 0,
					pageSize: 9999,
					programId: program._id,
				});

				setRecplaySegmentTemplates(res.data.templates);
			} catch (error) {
				setRecplaySegmentTemplates([]);
			}
		};

		if (user && program) {
			fetchProgramElements();
			fetchRecplaySegmentTemplates();
		}
	}, [user, program, setElements]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando...' message='Por favor, aguarde...' />;
	}

	return (
		<>
			<Meta title='Editar modelo' />

			<PageHeader title='Editar Modelo' breadcrumb={breadcrumb}>
				{!script?._id && (
					<>
						<Divider />

						<Form {...layout}>
							<Form.Item required label='Selecione o usuário'>
								<Select
									showSearch
									optionFilterProp='children'
									placeholder='Selecione o usuário'
									value={user ? JSON.stringify(user) : undefined}
									onChange={(value) => setUser(JSON.parse(value))}
									filterOption={(input, { props: { _search } }) => {
										const regex = new RegExp(input, 'i');
										return _search.match(regex);
									}}>
									{users.map((user) => {
										const { radioName, city, state, email } = user;

										return (
											<Select.Option
												_search={`${radioName}${city}${state}${email}`}
												key={user?._id}
												value={JSON.stringify(user)}>
												{radioName} - {city}/{state} ({email})
											</Select.Option>
										);
									})}
								</Select>
							</Form.Item>

							<Form.Item required label='Selecione o programa'>
								<Spin spinning={fallback?.fetchingPrograms === true} tip='Buscando programas...'>
									<Select
										showSearch
										disabled={!user}
										optionFilterProp='children'
										placeholder='Selecione o programa'
										value={program ? program?._id : undefined}
										onChange={(value) => {
											const _program = programs.find((p) => p?._id === value);

											setProgram({
												..._program,
												isTalkProgram: _program?.userId === env.talkId,
											});
										}}
										filterOption={(input, { props: { children } }) => {
											const regex = new RegExp(input, 'i');

											return children.match(regex);
										}}>
										{programs.map((program) => (
											<Select.Option key={program._id} value={program._id}>
												{program.name}
											</Select.Option>
										))}
									</Select>
								</Spin>
							</Form.Item>

							<Form.Item required label='Selecione o modelo'>
								<Spin
									spinning={fallback?.fetchingProgramElements === true}
									tip='Buscando modelos...'>
									<Select
										showSearch
										disabled={!program}
										value={scriptId}
										placeholder='Selecione o modelo'
										onChange={(script) => {
											script = JSON.parse(script);
											setScript(script);
											setScriptId(script?._id);
										}}>
										{scripts.map((script) => (
											<Select.Option key={script._id} value={JSON.stringify(script)}>
												{script.name}
											</Select.Option>
										))}
									</Select>
								</Spin>
							</Form.Item>
						</Form>
					</>
				)}
			</PageHeader>

			<Container isSorting={isSorting}>
				{script?._id && (
					<>
						<Card>
							<Grid>
								<ElementsTabs
									isAdmin={true}
									programId={program?._id}
									tkVoiceTypes={tkVoiceTypes}
									recplaySegmentTemplates={recplaySegmentTemplates}
								/>

								<ScriptWrapper>
									<Typography.Title level={4}>Modelo</Typography.Title>

									<Divider style={{ margin: '8px 0' }} />

									<OptionsContainer>
										<Button
											style={{ flex: 1 }}
											size='small'
											disabled={script?.body?.length === 0 || script?.isShared}
											onClick={() => setShowConfigStrsModal(true)}>
											<FiSettings /> Configurar trilhas sonoras
										</Button>

										<div className='divider'></div>

										<Tooltip title={compact ? 'Maximizar' : 'Compactar'}>
											<Button
												size='small'
												onClick={() => setCompact(!compact)}
												disabled={script?.body?.length === 0}>
												{compact ? <FiMaximize /> : <FiMinimize />}
											</Button>
										</Tooltip>

										<Tooltip
											title={script?.body?.length !== 0 && !script?.isShared && 'Limpar modelo'}>
											<Button
												disabled={script?.body?.length === 0 || script?.isShared}
												type='danger'
												size='small'
												onClick={() => {
													Modal.confirm({
														title: 'Limpar modelo?',
														icon: 'exclamation-circle',
														content:
															'Todos os blocos e elementos serão removidos, deseja continuar?',
														okText: 'Limpar modelo',
														onOk: () => setScript({ ...script, body: [] }),
														okButtonProps: {
															icon: 'delete',
															type: 'danger',
														},
														cancelText: 'Cancelar',
														cancelButtonProps: {
															icon: 'close-circle',
														},
													});
												}}>
												<FiXSquare />
											</Button>
										</Tooltip>
									</OptionsContainer>

									<Divider style={{ margin: '8px 0' }} />

									<Script
										isAdmin={true}
										onSortEnd={onElementMove}
										loggedUser={user}
										showInactiveElements={false}
										tkVoiceTypes={tkVoiceTypes}
										onSortStart={() => setIsSorting(true)}
										recplaySegmentTemplates={recplaySegmentTemplates}
									/>
								</ScriptWrapper>
							</Grid>

							<ButtonContainer>
								<Button
									type='primary'
									disabled={script.body.length === 0}
									onClick={handleUpdateScript}
									loading={fallback?.updatingScript}>
									<FiSave /> Atualizar modelo
								</Button>
							</ButtonContainer>
						</Card>

						<ConfigScriptStrs
							visible={showConfigStrsModal}
							onConfirm={() => setShowConfigStrsModal(false)}
							recplaySegmentTemplates={recplaySegmentTemplates}
						/>
					</>
				)}
			</Container>
		</>
	);
};

export default EditScript;
